
import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, ArrowDown, Loader2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/contexts/LanguageContext';
import { askLegalQuestion, DocumentRecommendation } from '@/services/chatbotService';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import TextToSpeech from './TextToSpeech';

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  recommendations?: DocumentRecommendation[];
}

const LegalChatbot: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const { t, language } = useLanguage();

  // Add welcome message when component mounts
  useEffect(() => {
    const welcomeMessage = language === 'ur' 
      ? 'آداب! میں آپ کا قانونی معاون ہوں۔ پاکستانی قانونی نظام کے بارے میں آپ کا کوئی بھی سوال پوچھیں اور میں آپ کی مدد کرنے کی کوشش کروں گا۔'
      : 'Hello! I am your legal assistant. Ask me any question about the Pakistani legal system and I will try to help you.';
    
    setMessages([{ 
      id: 'welcome', 
      content: welcomeMessage,
      isUser: false,
      timestamp: new Date()
    }]);
  }, [language]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check if scroll button should be shown
  useEffect(() => {
    const handleScroll = () => {
      if (!messagesContainerRef.current) return;
      
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom);
    };

    const messagesContainer = messagesContainerRef.current;
    if (messagesContainer) {
      messagesContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (messagesContainer) {
        messagesContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!input.trim()) return;
    
    const userMessage: Message = {
      id: Date.now().toString(),
      content: input,
      isUser: true,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    try {
      const response = await askLegalQuestion(input, language);
      
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: response.content,
        isUser: false,
        timestamp: new Date(),
        recommendations: response.documentRecommendations
      };
      
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error getting response:', error);
      toast.error(language === 'ur' ? 'معذرت، کوئی خرابی پیش آ گئی' : 'Sorry, an error occurred');
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: language === 'ur' 
          ? 'معذرت، میں اس وقت آپ کے سوال کا جواب نہیں دے سکتا۔ براہ کرم بعد میں دوبارہ کوشش کریں۔' 
          : 'Sorry, I cannot answer your question at the moment. Please try again later.',
        isUser: false,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Format message content with better styling
  const formatMessageContent = (content: string) => {
    // Split content by newlines and create paragraph elements
    return content.split('\n').map((paragraph, index) => (
      paragraph ? <p key={index} className="mb-1 last:mb-0">{paragraph}</p> : <br key={index} />
    ));
  };

  const handleDocumentSelect = (recommendation: DocumentRecommendation) => {
    // Handle document selection - navigate to template or pre-fill form
    toast.success(
      language === 'ur' 
        ? `دستاویز "${recommendation.title}" تیار کی جا رہی ہے` 
        : `Preparing document "${recommendation.title}"`, 
      { duration: 2000 }
    );
    // You can add navigation or other actions here
  };

  return (
    <div className="flex flex-col h-[70vh] bg-white rounded-lg shadow-md overflow-hidden border border-blue-100">
      {/* Chat header with animation */}
      <div className="bg-gradient-to-r from-primary to-blue-600 text-white px-4 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center bg-white bg-opacity-20 rounded-full p-1.5">
            <Bot size={18} />
          </div>
          <div>
            <h2 className="font-semibold">{t('chatbot.title')}</h2>
            <p className="text-xs text-blue-100 opacity-90">
              {language === 'ur' ? 'پاکستانی قانونی نظام کے بارے میں سوالات پوچھیں' : 'Ask questions about Pakistani legal system'}
            </p>
          </div>
        </div>
      </div>
      
      {/* Messages container */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 p-4 overflow-y-auto bg-gradient-to-b from-blue-50 to-slate-50"
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={`mb-4 flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={cn(
                "max-w-[80%] rounded-lg px-4 py-2 shadow-sm",
                message.isUser
                  ? "bg-primary text-primary-foreground"
                  : "bg-white border border-gray-100"
              )}
            >
              <div className="flex flex-col">
                <div className="mb-1">
                  {formatMessageContent(message.content)}
                </div>
                
                {/* Document recommendations */}
                {message.recommendations && message.recommendations.length > 0 && (
                  <div className="mt-3 border-t border-gray-200 pt-2">
                    <p className="text-sm font-medium mb-2">
                      {language === 'ur' ? 'تجویز کردہ دستاویزات:' : 'Recommended Documents:'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {message.recommendations.map((rec, index) => (
                        <Button 
                          key={index} 
                          variant="outline" 
                          size="sm" 
                          className="text-xs flex items-center gap-1 bg-blue-50 hover:bg-blue-100"
                          onClick={() => handleDocumentSelect(rec)}
                        >
                          <FileText size={14} />
                          {rec.title}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Add TextToSpeech button for AI messages */}
                {!message.isUser && (
                  <div className="mt-2 flex justify-end">
                    <TextToSpeech text={message.content} />
                  </div>
                )}
                
                <div className={cn(
                  "text-[10px] mt-1 self-end",
                  message.isUser ? "text-blue-100" : "text-gray-400"
                )}>
                  {format(message.timestamp, 'h:mm a')}
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start mb-4">
            <div className="max-w-[80%] rounded-lg px-4 py-2 bg-white border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  {language === 'ur' ? 'تیار کر رہا ہے' : 'Typing'}
                </span>
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" />
                  <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                  <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Scroll to bottom button */}
      {showScrollButton && (
        <Button
          onClick={scrollToBottom}
          size="sm"
          variant="secondary"
          className="absolute bottom-24 right-6 rounded-full w-10 h-10 shadow-md"
        >
          <ArrowDown size={18} />
        </Button>
      )}
      
      {/* Input area */}
      <div className="border-t bg-white p-4">
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chatbot.inputPlaceholder')}
            className={`flex-1 resize-none border-blue-100 focus:border-blue-300 focus:ring-blue-200 ${language === 'ur' ? 'text-right' : 'text-left'}`}
            rows={2}
            disabled={isLoading}
          />
          <Button
            onClick={handleSendMessage}
            disabled={!input.trim() || isLoading}
            className="self-end h-10 w-10 rounded-full"
            size="icon"
          >
            {isLoading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Send size={18} />
            )}
            <span className="sr-only">{t('chatbot.send')}</span>
          </Button>
        </div>
        <div className="mt-2 text-xs text-gray-400 text-center">
          {language === 'ur' 
            ? 'یہ عام معلومات ہے، قانونی مشورے کے لیے کسی وکیل سے رجوع کریں' 
            : 'This is general information, consult a lawyer for legal advice'}
        </div>
      </div>
    </div>
  );
};

export default LegalChatbot;
